import { useNotificationProvider } from 'hooks';
import { cloneDeep } from 'lodash';
import { useCallback, useEffect, useState } from 'react';

const useCheckpointEditor = (
	highlightedCheckpoint: Checkpoint,
	setCheckpoints: React.Dispatch<React.SetStateAction<Checkpoint[]>>,
) => {
	const [disabled, setDisabled] = useState(true);
	const { notifyError } = useNotificationProvider();

	useEffect(() => {
		if (highlightedCheckpoint.CheckpointID !== 0) {
			setDisabled(false);
		} else {
			setDisabled(true);
		}
	}, [disabled, highlightedCheckpoint]);

	const onUpdateCheckpoint = useCallback((checkpoint: Checkpoint) => {
		setCheckpoints((prev: any) => {
			return prev.map((currentCheckpoint: Checkpoint) => {
				if (
					currentCheckpoint.CheckpointID === checkpoint.CheckpointID
				) {
					return checkpoint;
				}
				return currentCheckpoint;
			});
		});
	}, []);

	const handleNameChange = useCallback(
		(e: any) => {
			onUpdateCheckpoint({
				...highlightedCheckpoint,
				Name: e.target.value,
			});
		},
		[highlightedCheckpoint, onUpdateCheckpoint],
	);

	const handleDescriptionChange = useCallback(
		(e: any) => {
			onUpdateCheckpoint({
				...highlightedCheckpoint,
				Description: e.target.value,
			});
		},
		[highlightedCheckpoint, onUpdateCheckpoint],
	);

	const handleResponseTypeChange = useCallback(
		(e: any) => {
			const hasDropdownError = highlightedCheckpoint.Dropdowns.filter(
				(dropdown) => dropdown.isError,
			);
			if (hasDropdownError.length > 0) {
				notifyError('Please fill or discard dropdown names');
			} else {
				onUpdateCheckpoint({
					...highlightedCheckpoint,
					ResponseType: e.target.value,
				});
			}
		},
		[highlightedCheckpoint, onUpdateCheckpoint],
	);

	const handleStatusChange = (_status: CheckpointStatus[]) => {
		const newStatus = cloneDeep(_status);
		const updatedCheckpoint: Checkpoint = {
			...highlightedCheckpoint,
			Status: newStatus,
		};
		onUpdateCheckpoint(updatedCheckpoint);
	};

	const handleDropdownChange = (dropdowns: CheckpointDropdown[]) => {
		const newDropdowns = cloneDeep(dropdowns);
		const updatedCheckpoint: Checkpoint = {
			...highlightedCheckpoint,
			Dropdowns: newDropdowns,
		};
		onUpdateCheckpoint(updatedCheckpoint);
	};

	const state = {
		disabled,
	};
	const handlers = {
		handleNameChange,
		handleDescriptionChange,
		handleResponseTypeChange,
		handleStatusChange,
		handleDropdownChange,
	};

	return { state, handlers };
};

export default useCheckpointEditor;
